/**
 * AssistantService — client for the AI assistant chat endpoints of one
 * {@link AssistantScope} (a network or a Frontdoor account),
 * served by the standalone `ai-assistant` service off `environment.aiAssistantUrl`
 * rather than by any of the core APIs.
 *
 * Chat streams from `POST .../chat/stream` as Server-Sent Events. Streaming
 * uses `fetch` rather than HttpClient: Angular's HttpClient cannot consume a
 * POST + text/event-stream response incrementally. That means the bearer
 * token must be attached manually here (the Auth0 HTTP interceptor only covers
 * HttpClient traffic). The non-streaming DELETE goes through HttpClient as
 * usual.
 *
 * Every SSE event carries a single-line JSON payload and a type:
 *   - `message` — `{"text": "..."}`, the next fragment of the reply text
 *   - `chart`   — a complete AssistantChart to render; always sent before
 *                 the reply text that references its `[chart:<id>]` marker
 *   - `changed` — `{"tool": "..."}`, a tool that may change data has run
 *   - `table`   — a complete AssistantTable; sent before the text that
 *                 references its `[table:<id>]` marker
 *   - `question` — a ClarifyingQuestion the assistant asks back, with
 *                  suggested answers; the reply text states it too
 *
 * The conversation id — generated server-side when the request doesn't carry
 * one — is returned in the `X-Conversation-Id` response header; pass it back
 * on subsequent requests to keep context.
 */

import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { ENVIRONMENT, Environment } from '@netfoundry-ui/shared/model';
import { HTTP_CLIENT, TokenService } from '@netfoundry-ui/shared/services';
import { Auth0Service } from '@netfoundry-ui/shared/authorization';
import { AssistantScope, assistantScopeUrl } from './assistant-scope';

export interface ChatRequest {
    message: string;
    conversationId?: string;
    /** Typed answer to the previous reply's question, when it declared an input. The message carries it as text too. */
    answer?: QuestionAnswer;
}

/** A single point in a chart series — a UTC ISO-8601 timestamp and its value. */
export interface ChartPoint {
    timestamp: string;
    value: number;
}

/** One named line/area/bar series within a chart. */
export interface ChartSeries {
    name: string;
    points: ChartPoint[];
}

/** A time-series chart the assistant asked us to render. */
export interface TimeSeriesChartSpec {
    title: string;
    type: 'LINE' | 'AREA' | 'BAR';
    yAxisLabel?: string;
    series: ChartSeries[];
}

/**
 * A chart as delivered by the assistant: the spec plus the server-assigned
 * id its `[chart:<id>]` reply marker refers to. Charts persisted before ids
 * existed may lack one — they simply never match a marker and fall back to
 * rendering after the text.
 */
export interface AssistantChart extends TimeSeriesChartSpec {
    id?: string;
}

/**
 * A question the assistant asks back. CLARIFICATION resolves an ambiguous request; CONFIRMATION asks to go ahead with a
 * planned change and always offers "Yes, go ahead" then "No, cancel". Each option is a complete reply to send as the
 * next message.
 */
export interface ClarifyingQuestion {
    kind: 'CLARIFICATION' | 'CONFIRMATION';
    question: string;
    options: string[];
    /** The typed input the question asks for. Without it, the options are the answers. */
    input?: QuestionInput | null;
    /** For a CONFIRMATION, the steps of the change it asks to approve. */
    plan?: PlannedChange[];
}

/** A suggested length. `duration` is ISO-8601 in hours, minutes and seconds, e.g. PT168H. */
export interface Preset {
    label: string;
    duration: string;
}

export interface ResourceCandidate {
    id: string;
    name: string;
    detail?: string | null;
}

export type AttributeMode = 'ATTRIBUTES' | 'ROLES';

export type TextFormat = 'HOSTNAME' | 'IP' | 'CIDR' | 'HOST_PORT' | 'PORT' | 'DOMAIN_NAME' | 'EMAIL';

export type QuestionInput =
    | { type: 'TIME_RANGE'; presets: Preset[] }
    | { type: 'RESOURCE'; kind: string; multiple: boolean; candidates: ResourceCandidate[] }
    | { type: 'DURATION'; presets: Preset[]; min: string; max: string }
    | {
          type: 'NUMBER';
          unit?: string | null;
          min?: number | null;
          max?: number | null;
          integer: boolean;
          suggested?: number | null;
      }
    | { type: 'ATTRIBUTES'; mode: AttributeMode; current: string[] }
    | { type: 'TEXT'; format: TextFormat; suggested?: string | null };

/** One step of the change a confirmation asks to approve. It is what the assistant says it will do. */
export interface PlannedChange {
    action: 'CREATE' | 'UPDATE' | 'DELETE';
    kind: string;
    name: string;
    id?: string | null;
    changes: { field: string; before?: string | null; after?: string | null }[];
}

/** A typed answer. `from` and `to` are UTC ISO-8601, and `from` must be before `to`. */
export interface TimeRangeAnswer {
    type: 'TIME_RANGE';
    from: string;
    to: string;
}

/**
 * The answer to a CONFIRMATION question: true for its first option, "Yes, go ahead"; false for "No, cancel". `discuss`
 * marks a typed reply about the plan, which leaves it unconfirmed.
 */
export interface ConfirmationAnswer {
    type: 'CONFIRMATION';
    confirmed: boolean;
    discuss?: boolean;
}

export type QuestionAnswer =
    | TimeRangeAnswer
    | ConfirmationAnswer
    | { type: 'RESOURCE'; kind: string; ids: string[] }
    | { type: 'DURATION'; duration: string }
    | { type: 'NUMBER'; value: number }
    | { type: 'ATTRIBUTES'; mode: AttributeMode; values: string[] }
    | { type: 'TEXT'; format: TextFormat; value: string };

/** A table the assistant rendered. With a `kind`, a row's `id` names that kind of resource. */
export interface AssistantTable {
    id: string;
    title: string;
    kind?: string | null;
    columns: string[];
    rows: { id?: string | null; cells: string[] }[];
}

export interface StreamChatHandlers {
    /** Fires once per `message` event with the next fragment of the reply text. */
    onDelta: (text: string) => void;
    /** Fires once per `chart` event, always before the text that references it. */
    onChart: (chart: AssistantChart) => void;
    /** Fires once per `changed` event, after a tool that may change data has run, with the tool's name. */
    onChanged?: (tool: string) => void;
    /** Fires once per `table` event, always before the text that references it. */
    onTable?: (table: AssistantTable) => void;
    /** Fires at most once per reply, when the assistant asks a question back. */
    onQuestion?: (question: ClarifyingQuestion) => void;
    /**
     * Fires once, as soon as response headers arrive, with the conversation id
     * from `X-Conversation-Id`.
     */
    onConversationId?: (id: string) => void;
}

/** One decoded SSE event: its type (`event:` field) and raw data payload. */
export interface SseEvent {
    event: string;
    data: string;
}

/**
 * Incremental decoder for a text/event-stream body. Feed it raw text chunks
 * as they arrive; it returns each complete event's type and `data:` payload.
 * Multi-line payloads (consecutive `data:` lines in one event) are joined
 * with `\n` per the SSE spec. Events without an `event:` field get the
 * spec's default type `message`.
 *
 * Spec deviation, on purpose: the SSE spec says a single space after the
 * `data:` colon is a separator and must be stripped. Spring (our backend)
 * never writes that separator — everything after the colon is payload — so
 * this decoder keeps every character after the colon. The payloads are JSON,
 * which tolerates leading whitespace anyway.
 */
export class SseDecoder {
    private buffer = '';

    push(text: string): SseEvent[] {
        this.buffer += text;
        const events: SseEvent[] = [];
        // Normalize CRLF so the event delimiter is always "\n\n".
        this.buffer = this.buffer.replace(/\r\n/g, '\n');
        let idx: number;
        while ((idx = this.buffer.indexOf('\n\n')) !== -1) {
            const raw = this.buffer.slice(0, idx);
            this.buffer = this.buffer.slice(idx + 2);
            const event = this.decodeEvent(raw);
            if (event !== null) {
                events.push(event);
            }
        }
        return events;
    }

    private decodeEvent(raw: string): SseEvent | null {
        const lines = raw.split('\n');
        const dataLines = lines.filter((line) => line.startsWith('data:')).map((line) => line.slice(5));
        if (dataLines.length === 0) {
            return null;
        }
        const eventLine = lines.find((line) => line.startsWith('event:'));
        // Unlike data, a leading space in the event name is never payload.
        const event = eventLine ? eventLine.slice(6).trim() : 'message';
        return { event, data: dataLines.join('\n') };
    }
}

/** Thrown when the stream endpoint responds with a non-2xx status. */
export class AssistantHttpError extends Error {
    constructor(
        public readonly status: number,
        /** Parsed response body, if it was JSON (RFC 7807 problem detail). */
        public readonly body: unknown,
        message: string
    ) {
        super(message);
    }
}

@Injectable({ providedIn: 'root' })
export class AssistantService {
    constructor(
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment,
        private auth0Service: Auth0Service,
        private tokenService: TokenService
    ) {}

    /**
     * The scope's root in the standalone `ai-assistant` service. There is no
     * `/assistant` segment: the chat endpoints hang directly off
     * `/networks/{id}` or `/frontdoors/{id}`.
     */
    private assistantUrl(scope: AssistantScope): string {
        return assistantScopeUrl(this.environment.aiAssistantUrl, scope);
    }

    /**
     * Send a message and stream the reply. Resolves when the stream ends;
     * rejects on HTTP errors (AssistantHttpError) or network/abort failures.
     */
    async streamChat(
        scope: AssistantScope,
        request: ChatRequest,
        handlers: StreamChatHandlers,
        signal?: AbortSignal
    ): Promise<void> {
        const token = await this.bearerToken();
        const response = await fetch(`${this.assistantUrl(scope)}/chat/stream`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(request),
            signal,
        });

        if (!response.ok) {
            let body: unknown = null;
            try {
                body = await response.json();
            } catch {
                /* non-JSON error body — status alone will have to do */
            }
            throw new AssistantHttpError(
                response.status,
                body,
                `Assistant request failed with status ${response.status}`
            );
        }

        const conversationId = response.headers.get('X-Conversation-Id');
        if (conversationId) {
            handlers.onConversationId?.(conversationId);
        }

        if (!response.body) {
            return;
        }

        const reader = response.body.getReader();
        const textDecoder = new TextDecoder();
        const sse = new SseDecoder();
        try {
            for (;;) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }
                for (const event of sse.push(textDecoder.decode(value, { stream: true }))) {
                    this.dispatch(event, handlers);
                }
            }
        } finally {
            reader.releaseLock();
        }
    }

    /**
     * The token for the streaming request. Asked for silently first, so an
     * access token that expired mid-session is refreshed rather than sent
     * stale — the interceptor does the same for HttpClient traffic, and this
     * request never reaches it. The cached token is the fallback for when the
     * SDK cannot answer (no session yet, refresh in flight).
     */
    private async bearerToken(): Promise<string | undefined> {
        try {
            const token = await firstValueFrom(this.auth0Service.getAccessTokenSilently());
            if (typeof token === 'string' && token) {
                return token;
            }
        } catch {
            /* fall back to whatever is cached */
        }
        return this.tokenService.getAccessToken()?.accessToken;
    }

    /**
     * Route one decoded event to its handler. A payload that fails to parse —
     * or an event type this client doesn't know — is skipped rather than
     * aborting the stream: losing one fragment beats losing the reply.
     */
    private dispatch(event: SseEvent, handlers: StreamChatHandlers): void {
        let payload: unknown;
        try {
            payload = JSON.parse(event.data);
        } catch {
            console.warn('[assistant] skipping malformed stream event payload:', event);
            return;
        }
        if (event.event === 'message') {
            const text = (payload as { text?: string }).text;
            if (typeof text === 'string') {
                handlers.onDelta(text);
            }
        } else if (event.event === 'chart') {
            handlers.onChart(payload as AssistantChart);
        } else if (event.event === 'changed') {
            handlers.onChanged?.((payload as { tool?: string })?.tool ?? '');
        } else if (event.event === 'table') {
            const table = toAssistantTable(payload);
            if (table) {
                handlers.onTable?.(table);
            }
        } else if (event.event === 'question') {
            const question = toClarifyingQuestion(payload);
            if (question) {
                handlers.onQuestion?.(question);
            }
        }
    }

    /** Clear the assistant's memory of a conversation. */
    clearConversation(scope: AssistantScope, conversationId: string): Observable<void> {
        return this.http.delete<void>(`${this.assistantUrl(scope)}/chat/${encodeURIComponent(conversationId)}`);
    }
}

/** Accept a question payload only in its contracted shape, so a malformed event cannot render a broken chip. */
function toClarifyingQuestion(payload: unknown): ClarifyingQuestion | null {
    const candidate = payload as Partial<ClarifyingQuestion> | null;
    if (!candidate || typeof candidate.question !== 'string' || !Array.isArray(candidate.options)) {
        return null;
    }
    const options = candidate.options.filter(
        (option): option is string => typeof option === 'string' && option.trim() !== ''
    );
    // A confirmation is answered by option position, so one that does not carry exactly its two options is asked as
    // plain text.
    const confirmation = candidate.kind === 'CONFIRMATION' && candidate.options.length === 2 && options.length === 2;
    return {
        kind: confirmation ? 'CONFIRMATION' : 'CLARIFICATION',
        question: candidate.question,
        options,
        input: toQuestionInput(candidate.input),
        plan: Array.isArray(candidate.plan) ? candidate.plan.filter(isPlannedChange) : [],
    };
}

/**
 * An input of a type this client does not know, or one in the wrong shape, is dropped, so the question falls back to
 * its plain options.
 */
function toQuestionInput(input: unknown): QuestionInput | null {
    const candidate = input as Record<string, unknown> | null | undefined;
    const strings = (value: unknown): string[] =>
        Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
    const optionalNumber = (value: unknown): number | null =>
        typeof value === 'number' && isFinite(value) ? value : null;
    switch (candidate?.['type']) {
        case 'TIME_RANGE':
            return { type: 'TIME_RANGE', presets: presets(candidate['presets']) };
        case 'RESOURCE': {
            const candidates = Array.isArray(candidate['candidates'])
                ? (candidate['candidates'] as ResourceCandidate[]).filter(
                      (item) => typeof item?.id === 'string' && typeof item?.name === 'string'
                  )
                : [];
            if (typeof candidate['kind'] !== 'string' || candidates.length === 0) {
                return null;
            }
            return { type: 'RESOURCE', kind: candidate['kind'], multiple: candidate['multiple'] === true, candidates };
        }
        case 'DURATION':
            if (typeof candidate['min'] !== 'string' || typeof candidate['max'] !== 'string') {
                return null;
            }
            return {
                type: 'DURATION',
                presets: presets(candidate['presets']),
                min: candidate['min'],
                max: candidate['max'],
            };
        case 'NUMBER':
            return {
                type: 'NUMBER',
                unit: typeof candidate['unit'] === 'string' ? candidate['unit'] : null,
                min: optionalNumber(candidate['min']),
                max: optionalNumber(candidate['max']),
                integer: candidate['integer'] === true,
                suggested: optionalNumber(candidate['suggested']),
            };
        case 'ATTRIBUTES':
            if (candidate['mode'] !== 'ATTRIBUTES' && candidate['mode'] !== 'ROLES') {
                return null;
            }
            return { type: 'ATTRIBUTES', mode: candidate['mode'], current: strings(candidate['current']) };
        case 'TEXT':
            if (!TEXT_FORMATS.includes(candidate['format'] as TextFormat)) {
                return null;
            }
            return {
                type: 'TEXT',
                format: candidate['format'] as TextFormat,
                suggested: typeof candidate['suggested'] === 'string' ? candidate['suggested'] : null,
            };
        default:
            return null;
    }
}

const TEXT_FORMATS: TextFormat[] = ['HOSTNAME', 'IP', 'CIDR', 'HOST_PORT', 'PORT', 'DOMAIN_NAME', 'EMAIL'];

function presets(value: unknown): Preset[] {
    return Array.isArray(value)
        ? value.filter(
              (preset): preset is Preset => typeof preset?.label === 'string' && typeof preset?.duration === 'string'
          )
        : [];
}

function isPlannedChange(step: unknown): step is PlannedChange {
    const candidate = step as Partial<PlannedChange> | null;
    return (
        ['CREATE', 'UPDATE', 'DELETE'].includes(candidate?.action as string) &&
        typeof candidate?.kind === 'string' &&
        typeof candidate?.name === 'string' &&
        Array.isArray(candidate?.changes)
    );
}

/** A table whose rows do not line up with its columns is dropped rather than rendered misaligned. */
function toAssistantTable(payload: unknown): AssistantTable | null {
    const candidate = payload as Partial<AssistantTable> | null;
    if (
        !candidate ||
        typeof candidate.id !== 'string' ||
        !Array.isArray(candidate.columns) ||
        !Array.isArray(candidate.rows)
    ) {
        return null;
    }
    const width = candidate.columns.length;
    const rows = candidate.rows.filter((row) => Array.isArray(row?.cells) && row.cells.length === width);
    return {
        id: candidate.id,
        title: typeof candidate.title === 'string' ? candidate.title : '',
        kind: typeof candidate.kind === 'string' ? candidate.kind : null,
        columns: candidate.columns.map(toCellText),
        rows: rows.map((row) => ({ id: typeof row.id === 'string' ? row.id : null, cells: row.cells.map(toCellText) })),
    };
}

/** A cell as text; a missing value is an empty cell, not the word "null". */
function toCellText(value: unknown): string {
    return String(value ?? '');
}
